import React from "react";
import type { FC } from "react";

import { useIntl } from "react-intl";

import {
	BUTTON_SIZE_MD,
	BUTTON_VARIANT_SECONDARY,
	Button,
	Flex,
	LINK_VARIANT_DEFAULT,
	LINK_VARIANT_EXTRALIGHT,
	Link,
	Panel,
} from "mailup-web-components";

import FormContainer from "Components/FormContainer";

import IconPlus from "Components/icons/IconPlus";

import InputContact from "Pages/RecipientsList/components/filters/InputContact";
import {
	resetResultsCallback,
	searchResultsCallback,
} from "Pages/RecipientsList/recoil/callbacks/callbacks";

import { addCriterionCallback } from "Pages/RecipientsList/recoil/callbacks/filters";
import { modalSaveSegmentOpenCallback } from "Pages/RecipientsList/recoil/callbacks/modalSaveSegment";

import {
	canResetSearch,
	searchFiltersEnabledSubmitState,
	searchFiltersState,
} from "Pages/RecipientsList/recoil/state/filters";
import { gainsightTrack } from "mailup-react-helpers";
import { useRecoilCallback, useRecoilValue } from "recoil";
import Criteria from "./filters/Criteria";

const BoxFilters: FC = () => {
	const intl = useIntl();

	const canSaveSegment = false;

	const submitEnabled = useRecoilValue(searchFiltersEnabledSubmitState);
	const canReset = useRecoilValue(canResetSearch);

	const searchResults = useRecoilCallback(searchResultsCallback, []);
	const resetResults = useRecoilCallback(resetResultsCallback, []);
	const addCriterion = useRecoilCallback(addCriterionCallback, []);

	const openSaveSegment = useRecoilCallback(modalSaveSegmentOpenCallback, []);
	const { contact } = useRecoilValue(searchFiltersState);
	const handleAddFilter = (): void => {
		gainsightTrack("mup_filter_click_addRecipientsButton", {});
		void addCriterion();
	};

	const handleSearch = (
		e: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>,
	): void => {
		e.preventDefault();

		gainsightTrack("mup_recipients_click_searchButton", {
			searchWithTextInput: contact.value !== "",
		});

		void searchResults();
	};

	const handleReset = (): void => {
		gainsightTrack("mup_recipients_click_resetFilters", {});
		void resetResults();
	};

	const handleOpenSaveSegment = (
		e: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>,
	): void => {
		e.preventDefault();

		void openSaveSegment();
	};

	return (
		<Panel margin="0">
			<FormContainer canSubmit={submitEnabled} onSubmit={handleSearch}>
				<Flex flexDirection="column" gap="16px">
					<InputContact />

					<Criteria />

					<div>
						<Link
							variant={LINK_VARIANT_DEFAULT}
							data-mu-test="recipients-add-filter"
							onClick={handleAddFilter}
							alignContent
							gap="4px"
						>
							<IconPlus width="12" height="16" />
							{intl.formatMessage({ id: "Load.RecipientsList.AddFilter" })}
						</Link>
					</div>

					<Flex flexDirection="row" alignItems="center" gap="16px">
						<Button
							kind={BUTTON_VARIANT_SECONDARY}
							size={BUTTON_SIZE_MD}
							disabled={!submitEnabled}
							onClick={handleSearch}
							data-mu-test="recipients-search"
						>
							{intl.formatMessage({ id: "Load.RecipientsList.Search" })}
						</Button>

						{canReset && (
							<Link
								variant={LINK_VARIANT_EXTRALIGHT}
								onClick={handleReset}
								data-mu-test="recipients-reset"
							>
								{intl.formatMessage({
									id: "Load.RecipientsList.ResetSearchCriteria",
								})}
							</Link>
						)}
					</Flex>
				</Flex>
			</FormContainer>
		</Panel>
	);
};

export default BoxFilters;
